import { option } from 'fp-ts';
import { flow } from 'fp-ts/function';
import { concat, get, isEmpty, reject } from 'lodash/fp';
import { createSelector } from 'reselect';

import { convertGrossAmountToSimpleAmount, convertNetAmountToSimpleAmount } from '@mews-distributor/common';
import { composeProps, createPropsExtender } from '@mews/framework';

import {
    getConfigurationCheckoutStatement,
    getSelectedHotelConfigurationId,
} from '../../../modules/configuration/selectors';
import {
    getEnterpriseAdditionalLegalStatements,
    getEnterpriseAddressCountryCodeOption,
    getEnterpriseCityId,
    getEnterpriseDefaultCurrencyCode,
    getEnterpriseDefaultLanguageCode,
    getEnterpriseDefaultRateCurrencyCode,
    getEnterpriseEmail,
    getEnterpriseImageId,
    getEnterpriseIntroImageId,
    getEnterpriseName,
    getEnterprisePricingModeKey,
    getEnterpriseSmsConsent,
    getEnterpriseTelephone,
    getEnterpriseTimezone,
    hasEnterpriseData,
    hasEnterpriseNetPricing,
} from '../../../modules/entities/enterprises/selectors';
import { getSelectedHotelId, selectedEnterpriseIdPropsExtender } from '../../../modules/resourceOrder/selectors';
import { ApplicationState } from '../../../types';
import { getCountryCodeCheckoutStatements } from './countryCodeStatements';

/*
 * If possible, avoid using the contextual selectors from this file in new code. We should instead prefer context-less selectors. It makes code more predictable, testable and components more reusable.
 * For example instead of using wrapper selector `getSelectedEnterpriseTimezone`, prefer directly using `getEnterpriseTimezone`.
 */

export const hasSelectedEnterpriseNetPricing = composeProps(hasEnterpriseNetPricing, selectedEnterpriseIdPropsExtender);
export const getSelectedEnterprisePricingModeKey = composeProps(
    getEnterprisePricingModeKey,
    selectedEnterpriseIdPropsExtender
);

export const getSelectedEnterpriseName = composeProps(getEnterpriseName, selectedEnterpriseIdPropsExtender);

export const getSelectedEnterpriseDefaultCurrencyCode = composeProps(
    getEnterpriseDefaultCurrencyCode,
    selectedEnterpriseIdPropsExtender
);

const getSelectedEnterpriseAddressCountryCodeOption = (state: ApplicationState) => {
    const enterpriseId = getSelectedHotelId(state);
    return getEnterpriseAddressCountryCodeOption(state, { enterpriseId });
};

export const getSelectedEnterpriseTimezone = composeProps(getEnterpriseTimezone, selectedEnterpriseIdPropsExtender);
export const getSelectedEnterpriseDefaultLanguageCode = composeProps(
    getEnterpriseDefaultLanguageCode,
    selectedEnterpriseIdPropsExtender
);
export const getSelectedEnterpriseDefaultRateCurrencyCode = composeProps(
    getEnterpriseDefaultRateCurrencyCode,
    selectedEnterpriseIdPropsExtender
);

export const getSelectedEnterpriseImageId = composeProps(getEnterpriseImageId, selectedEnterpriseIdPropsExtender);
export const getSelectedEnterpriseIntroImageId = composeProps(
    getEnterpriseIntroImageId,
    selectedEnterpriseIdPropsExtender
);

const getSelectedEnterpriseAdditionalLegalStatements = composeProps(
    getEnterpriseAdditionalLegalStatements,
    selectedEnterpriseIdPropsExtender
);

const getSelectedHotelConfigurationIdPropsExtender = createPropsExtender(
    'configurationId',
    getSelectedHotelConfigurationId
);

const getSelectedEnterpriseConfigurationCheckoutStatement = composeProps(
    getConfigurationCheckoutStatement,
    getSelectedHotelConfigurationIdPropsExtender
);

export const getSelectedEnterpriseCustomCheckoutLegalStatements = flow(
    getSelectedEnterpriseAddressCountryCodeOption,
    option.map(getCountryCodeCheckoutStatements),
    option.getOrElse<Mews.TextKey[]>(() => [])
);

export const getSelectedEnterpriseLegalStatements = createSelector(
    getSelectedEnterpriseAdditionalLegalStatements,
    getSelectedEnterpriseConfigurationCheckoutStatement,
    flow(concat, reject(isEmpty))
);

export const getSelectedEnterpriseCityId = composeProps(getEnterpriseCityId, selectedEnterpriseIdPropsExtender);
export const getSelectedEnterpriseEmail = composeProps(getEnterpriseEmail, selectedEnterpriseIdPropsExtender);
export const getSelectedEnterpriseTelephone = composeProps(getEnterpriseTelephone, selectedEnterpriseIdPropsExtender);

export const getSelectedEnterpriseDefaultLanguageName = createSelector(
    getSelectedEnterpriseDefaultLanguageCode,
    getSelectedEnterpriseName,
    get
);

export const hasSelectedEnterpriseData = composeProps(hasEnterpriseData, selectedEnterpriseIdPropsExtender);

export const getSelectedEnterpriseAmountConvertor = composeProps(
    (state, { enterpriseId }) =>
        hasEnterpriseNetPricing(state, { enterpriseId })
            ? convertNetAmountToSimpleAmount
            : convertGrossAmountToSimpleAmount,
    selectedEnterpriseIdPropsExtender
);

export const getSelectedEnterpriseSmsConsent = composeProps(getEnterpriseSmsConsent, selectedEnterpriseIdPropsExtender);
